import { useState } from 'react';
import { Plus } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import LoadError from '@/components/ui/LoadError.tsx';
import { SectionTitle } from '@/components/ui/Title.tsx';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import useGetTourFinanceDashboard from '@/features/dashboard/tours/hooks/useGetTourFinanceDashboard.ts';
import useGetTourBudget from '@/features/dashboard/tourBudget/hooks/useGetTourBudget.ts';
import useRemoveTourBudget from '@/features/dashboard/tourBudget/hooks/useRemoveTourBudget.ts';
import SetTourBudgetModal from '@/features/dashboard/tourBudget/components/SetTourBudgetModal.tsx';
import { TourBudgetViewDto } from '@/features/dashboard/tourBudget/types/TourBudgetDto.ts';
import {
  budgetBreakdownsWithDeclared,
  budgetByCurrency,
  currenciesWithoutBudget,
  isBudgetDeclared,
} from '@/features/dashboard/tourBudget/types/TourBudgetDto.utils.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { getCurrencyLabel } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import {
  BUDGET_TAB_KEYS,
  FINANCES_TAB_KEYS,
} from '@/features/dashboard/attention/types/AttentionKey.utils.ts';
import {
  moneyMetricCurrencies,
  sortByCurrency,
} from '@/types/stats/metric.utils.ts';
import {
  tourFinancesRoute,
  tourRoute,
} from '@/features/dashboard/tours/routes/tourRoute.ts';
import { FinanceView } from '@/features/dashboard/tours/routes/tourFinanceView.ts';
import TourAttentionPanel from '@/features/dashboard/tours/components/attention/TourAttentionPanel.tsx';
import FinanceViewToggle from '@/features/dashboard/tours/components/finance/FinanceViewToggle.tsx';
import PaymentsBalanceCard from '@/features/dashboard/tours/components/finance/PaymentsBalanceCard.tsx';
import BookingValueCard from '@/features/dashboard/tours/components/finance/BookingValueCard.tsx';
import BudgetCard from '@/features/dashboard/tours/components/finance/BudgetCard.tsx';
import CostCategoryDonutCard from '@/features/dashboard/tours/components/finance/CostCategoryDonutCard.tsx';
import TourTransactionsTable from '@/features/dashboard/tours/components/TourTransactionsTable.tsx';
import TourExpensesTable from '@/features/dashboard/expenses/components/TourExpensesTable.tsx';
import TourFinancesTabSkeleton from '@/features/dashboard/tours/pages/TourFinancesTabSkeleton.tsx';

/** `null` znaczy „modal otwarty na zakładanie budżetu, waluta jeszcze niewybrana”. */
type BudgetModalState = { budget: TourBudgetViewDto | null };

/**
 * Finanse wyjazdu. Wpłaty i budżet czyta się z jednej odpowiedzi, bo to dwa
 * spojrzenia na te same pieniądze — przełącznik zmienia tylko to, co widać.
 *
 * Dashboard tylko czyta. Deklarację budżetu (i jej brak) rozstrzyga osobne
 * `GET /tours/{id}/budget`, bo to ono zna kwoty wpisane przez operatora.
 */
const TourFinancesTab = () => {
  const { tourId } = tourRoute.useParams();
  const search = tourFinancesRoute.useSearch();
  const { view } = search;
  const {
    data: finance,
    isPending: isFinancePending,
    isError: isFinanceError,
  } = useGetTourFinanceDashboard(tourId);
  const {
    data: declaredBudgets,
    isPending: isBudgetPending,
    isError: isBudgetError,
  } = useGetTourBudget(tourId);
  const removeBudget = useRemoveTourBudget(tourId);
  const { confirmDeleteWithCallback } = confirmDialogUseHook();
  const [budgetModal, setBudgetModal] = useState<BudgetModalState | null>(null);

  /*
   * Obie odpowiedzi wstrzymują ekran razem. Pusta lista budżetów znaczy „nie
   * ustawiono”, więc karta narysowana przed jej przyjściem gubi kosz i podaje
   * modalowi wyzerowane DTO — zapis nadpisałby istniejący budżet zerami.
   */
  if (isFinancePending || isBudgetPending) return <TourFinancesTabSkeleton />;
  if (isFinanceError || isBudgetError || !finance || !declaredBudgets)
    return <LoadError subject="finansów i budżetu wyjazdu" />;

  const availableCurrencies = currenciesWithoutBudget(declaredBudgets);
  const balances = sortByCurrency(finance.balances);
  const budgets = budgetBreakdownsWithDeclared(
    finance.budgets,
    declaredBudgets,
  );
  const costsByCategory = sortByCurrency(finance.costsByCategory);

  const openBudgetModal = (currency: Currency) =>
    setBudgetModal({
      budget: budgetByCurrency(declaredBudgets, currency) ?? {
        currency,
        fixedAmount: 0,
        perParticipantAmount: 0,
        actualParticipantCount: 0,
        plannedTotal: 0,
        committed: 0,
        spent: 0,
        planVsCommitted: 0,
        committedVsSpent: 0,
        byCategory: [],
      },
    });

  const confirmDelete = (currency: Currency) =>
    confirmDeleteWithCallback({
      title: 'Usuń budżet',
      description: (
        <>
          Czy na pewno chcesz usunąć budżet w walucie{' '}
          <b>{getCurrencyLabel(currency)}</b>? Wydatki zostaną nietknięte.
        </>
      ),
      confirmText: 'Usuń budżet',
      onConfirm: () => removeBudget.mutate(currency),
    });

  return (
    <div className="space-y-4">
      <FinanceViewToggle view={view} />

      {view === FinanceView.PAYMENTS ? (
        <>
          <TourAttentionPanel
            tourId={tourId}
            title="Wymaga uwagi — wpłaty"
            keys={FINANCES_TAB_KEYS}
          />

          <div className="grid gap-4 lg:grid-cols-2">
            {balances.map((balance) => (
              <PaymentsBalanceCard key={balance.currency} balance={balance} />
            ))}
            {moneyMetricCurrencies(finance.bookingValue).map((currency) => (
              <BookingValueCard
                key={currency}
                metrics={finance.bookingValue}
                currency={currency}
              />
            ))}
          </div>

          <TourTransactionsTable listSearch={search} />
        </>
      ) : (
        <>
          <TourAttentionPanel
            tourId={tourId}
            title="Wymaga uwagi — budżet"
            keys={BUDGET_TAB_KEYS}
          />

          <div className="flex items-center justify-between">
            <SectionTitle>Budżet</SectionTitle>
            {availableCurrencies.length > 0 && (
              <ActionButton
                tone="accent"
                size="xs"
                onClick={() => setBudgetModal({ budget: null })}
              >
                <Plus />
                Ustaw budżet
              </ActionButton>
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {budgets.map((budget) => {
              const declared = budgetByCurrency(
                declaredBudgets,
                budget.currency,
              );

              return (
                <BudgetCard
                  key={budget.currency}
                  budget={budget}
                  declared={declared ? isBudgetDeclared(declared) : false}
                  onEdit={() => openBudgetModal(budget.currency)}
                  onDelete={() => confirmDelete(budget.currency)}
                />
              );
            })}
          </div>

          {costsByCategory.length === 0 ? (
            <CostCategoryDonutCard costs={null} />
          ) : (
            costsByCategory.map((costs) => (
              <CostCategoryDonutCard key={costs.currency} costs={costs} />
            ))
          )}

          <TourExpensesTable tourId={tourId} listSearch={search} />
        </>
      )}

      {budgetModal && (
        <SetTourBudgetModal
          tourId={tourId}
          budget={budgetModal.budget}
          availableCurrencies={availableCurrencies}
          onClose={() => setBudgetModal(null)}
        />
      )}
    </div>
  );
};

export default TourFinancesTab;
